// Stationary Fuel-Cell Feasibility Integration (Stage A + Stage B Vertical Slice)
//
// Implements primary-source-grounded stationary fuel-cell engineering boundaries,
// dimensional conversions, LTSA vs. discrete stack replacement mutual exclusion,
// closed thermal/water balances, island-readiness checks, IRC §48E(j) policy
// conservatism, and finance pathway presets for SOFC, PAFC, PEMFC, and MCFC.

import type {
  FuelCellAuxiliaryLoadKey,
  FuelCellDegradationSpec,
  FuelCellPhaseConfig,
  ProjectIntent,
  RatingBoundarySpec,
} from '../../../types/siteRag';
import type {
  FuelCellParameterProvenance,
  FuelSupplyEvidence,
} from '../../../types/siteEvidence';
import type { SiteContext } from '../../../types/siteContext';
import { DEFAULT_GAS_FUEL_PRICE_CAGR_PCT } from '../../inflationBasis';
import { KW_PER_MW } from '../../projectFinance';
import type {
  EconomicQualificationPosture,
  Pathway,
  PathwayRelianceHold,
  RefurbishmentEvent,
  ResolvedFuelCellEconomicSnapshot,
  TechnologyId,
} from '../types';
import {
  DEFAULT_BASE_WACC_PCT,
  annualMwhFromCf,
  buildCapexBlock,
  buildStandardBtmPhases,
  capexFromPerKw,
  computeAnnualOpexUsd,
  mergePathway,
  pathwayIdFromTechnology,
  resolveFacilityLoadMw,
  resolveProjectStartDate,
  valueOfServedLoadRevenue,
  type PathwayPresetOptions,
} from './shared';

/** Fuel-specific HHV/LHV ratios (Finding 11 — never apply NG ratio to H2). */
export const HHV_TO_LHV_RATIO_BY_FUEL: Readonly<
  Record<'natural-gas' | 'rng-biogas' | 'hydrogen' | 'hydrogen-ng-blend', number>
> = {
  'natural-gas': 1.108,
  'rng-biogas': 1.108,
  hydrogen: 1.182,
  'hydrogen-ng-blend': 1.123, // 20% H2 by volume blend reference
};

export interface CuratedFuelCellPackageSpec {
  packageId: string;
  label: string;
  manufacturer: string;
  model: string;
  technologyId: 'fuel-cell-sofc' | 'fuel-cell-pafc' | 'fuel-cell-pem' | 'fuel-cell-mcfc';
  chemistry: 'sofc' | 'pafc' | 'pem' | 'mcfc';
  primaryFuel: 'natural-gas' | 'rng-biogas' | 'hydrogen';
  moduleNetAcKw: number;
  ratingBoundary: RatingBoundarySpec;
  electricalEfficiencyFractionLhv: number;
  suppliedHeatRateHhvBtuPerKwh?: number;
  minInletPressure: { value: number; unit: 'psig' | 'in-wc' };
  maxInletPressure: { value: number; unit: 'psig' | 'in-wc' };
  degradation: FuelCellDegradationSpec;
  waterBalanceProfile: {
    zeroConsumptionBelowAmbientC: number | null;
    normalOperationalWaterDischargeGalPerMwh: number;
    makeupWaterAboveThresholdGalPerMwh: number;
    notes: string;
  };
  thermalDeliveryProfile: {
    internalStackTempRangeC: [number, number];
    usableExhaustFlangeTempC: number | null;
    usableJacketWaterTempC: number | null;
    recoverableExhaustFractionOfFuelLhv: number;
    recoverableJacketWaterFractionOfFuelLhv: number;
    internalRecuperationFractionOfFuelLhv: number;
  };
  benchmarks: {
    capexPerKwUsd: number;
    fixedOmPerKwYearUsd: number;
    ltsaPerKwYearUsd: number;
    variableOmPerMwhUsd: number;
    assumedBenchmarkDeliveredFuelUsdPerMmbtu: number;
    designLifeYears: number;
    availabilityPct: number;
    capacityFactorPct: number;
    capacityCreditPct: number;
    constructionMonths: number;
    riskPremiumBps: number;
    tCo2ePerMwh: number;
    decommPctOfCapex: number;
    contingencyPct: number;
    landAcresPerNameplateMw: number;
  };
  provenance: FuelCellParameterProvenance;
}

/**
 * Curated primary-source OEM / handbook packages (Findings 8, 9, 10, 11, 16).
 *
 * Citations:
 * - Bloom Energy Server 6.5 Datasheet (Feb 2026): 325 kW net AC skid boundary,
 *   cumulative electrical efficiency over contract term, 15-25 psig NG inlet.
 * - HyAxiom PureCell Model 400 NG Datasheet (Nov 2025): 460 kW net AC,
 *   first-year average efficiency, 10-14 in. w.c. inlet pressure, zero makeup
 *   water up to 30 °C (86 °F) ambient and zero normal operational water discharge.
 * - Stationary PEM reference (ISO 14687:2025 Grade E stationary / Grade D PEM):
 *   NREL / DOE stationary fuel-cell characterization (DOE Record #23002 excluded
 *   as heavy-duty truck only).
 * - MCFC reference: US EPA Catalog of CHP Technologies — Section 6: Fuel Cells
 *   (March 2015 publication date explicitly preserved).
 */
export const CURATED_FUEL_CELL_PACKAGES: Readonly<Record<string, CuratedFuelCellPackageSpec>> = {
  'bloom-energy-server-6.5-ng': {
    packageId: 'bloom-energy-server-6.5-ng',
    label: 'Bloom Energy Server 6.5 (SOFC, Natural Gas)',
    manufacturer: 'Bloom Energy',
    model: 'Energy Server 6.5 (325 kW block)',
    technologyId: 'fuel-cell-sofc',
    chemistry: 'sofc',
    primaryFuel: 'natural-gas',
    moduleNetAcKw: 325,
    ratingBoundary: {
      powerBoundary: 'net-ac-skid-boundary',
      efficiencyAveragingBasis: 'cumulative-contract-term',
      outputGuaranteeMode: 'constant-net-ac-via-fuel-trim',
      includedAuxiliaries: ['skid-blowers', 'internal-reformer', 'desulfurization'],
      isoReferenceConditions: { ambientTempC: 15, elevationM: 0, relativeHumidityPct: 60 },
      claimStatus: 'oem-published' as unknown as 'oem-indicative',
    },
    electricalEfficiencyFractionLhv: 0.53,
    minInletPressure: { value: 15, unit: 'psig' },
    maxInletPressure: { value: 25, unit: 'psig' },
    degradation: {
      degradationDriver: 'operating-hours',
      outputDeclineRate: { pctPer1000Hours: 0 }, // constant net AC via fuel trim under LTSA
      heatRateGrowthRate: { pctPer1000Hours: 0.12, pctPerYear: 1.0 },
      stackReplacementIntervalHours: 43800,
      stackReplacementIntervalYears: 5,
    },
    waterBalanceProfile: {
      zeroConsumptionBelowAmbientC: 40,
      normalOperationalWaterDischargeGalPerMwh: 0,
      makeupWaterAboveThresholdGalPerMwh: 0,
      notes: 'Requires no water during normal operation after initial startup charge (internal steam reforming recuperates product water).',
    },
    thermalDeliveryProfile: {
      internalStackTempRangeC: [650, 850],
      usableExhaustFlangeTempC: 230,
      usableJacketWaterTempC: null,
      recoverableExhaustFractionOfFuelLhv: 0.18,
      recoverableJacketWaterFractionOfFuelLhv: 0,
      internalRecuperationFractionOfFuelLhv: 0.22,
    },
    benchmarks: {
      capexPerKwUsd: 3200,
      fixedOmPerKwYearUsd: 45,
      ltsaPerKwYearUsd: 165, // covers hot-box / stack replacements under LTSA
      variableOmPerMwhUsd: 4.5,
      assumedBenchmarkDeliveredFuelUsdPerMmbtu: 5.0,
      designLifeYears: 20,
      availabilityPct: 95,
      capacityFactorPct: 95,
      capacityCreditPct: 95,
      constructionMonths: 14,
      riskPremiumBps: 100,
      tCo2ePerMwh: 0.38,
      decommPctOfCapex: 0.04,
      contingencyPct: 10,
      landAcresPerNameplateMw: 0.22,
    },
    provenance: {
      parameterKey: 'bloom-energy-server-6.5-ng',
      originAuthority: 'oem-published',
      verificationMethod: 'unverified-claim',
      siteApplicability: 'sku-specific-iso-rating',
      citation: {
        documentIdOrUrl: 'https://www.bloomenergy.com/wp-content/uploads/bloom-energy-server-datasheet-feb-2026.pdf',
        title: 'Bloom Energy Server 6.5 Datasheet',
        publicationOrRevisionDate: '2026-02',
        pageOrSection: 'Specifications Table, p. 2',
        exactClaimExcerpt: 'Nameplate power output: 325 kW net AC; Electrical efficiency (LHV): cumulative over contract period; Fuel pressure: 15-25 psig.',
        unitsAndConditions: '325 kW net AC at ISO conditions (15 °C, sea level); cumulative contract-term LHV efficiency.',
      },
      conflictStatus: 'active-authoritative',
    },
  },
  'hyaxiom-purecell-400-ng': {
    packageId: 'hyaxiom-purecell-400-ng',
    label: 'HyAxiom PureCell Model 400 (PAFC, Natural Gas)',
    manufacturer: 'HyAxiom (Doosan)',
    model: 'PureCell Model 400 NG (460 kW block)',
    technologyId: 'fuel-cell-pafc',
    chemistry: 'pafc',
    primaryFuel: 'natural-gas',
    moduleNetAcKw: 460,
    ratingBoundary: {
      powerBoundary: 'net-ac-skid-boundary',
      efficiencyAveragingBasis: 'first-year-average',
      outputGuaranteeMode: 'constant-net-ac-via-fuel-trim',
      includedAuxiliaries: ['skid-blowers', 'internal-reformer', 'desulfurization'],
      isoReferenceConditions: { ambientTempC: 15, elevationM: 0, relativeHumidityPct: 60 },
      claimStatus: 'oem-indicative',
    },
    electricalEfficiencyFractionLhv: 0.42,
    minInletPressure: { value: 10, unit: 'in-wc' },
    maxInletPressure: { value: 14, unit: 'in-wc' },
    degradation: {
      degradationDriver: 'operating-hours',
      outputDeclineRate: { pctPer1000Hours: 0 },
      heatRateGrowthRate: { pctPer1000Hours: 0.15, pctPerYear: 1.2 },
      stackReplacementIntervalHours: 87600,
      stackReplacementIntervalYears: 10,
    },
    waterBalanceProfile: {
      zeroConsumptionBelowAmbientC: 30,
      normalOperationalWaterDischargeGalPerMwh: 0,
      makeupWaterAboveThresholdGalPerMwh: 18,
      notes: 'Requires no makeup water consumption up to 30 °C (86 °F) ambient and produces no normal operational water discharge (HyAxiom Nov 2025 datasheet).',
    },
    thermalDeliveryProfile: {
      internalStackTempRangeC: [180, 210],
      usableExhaustFlangeTempC: 120,
      usableJacketWaterTempC: 60,
      recoverableExhaustFractionOfFuelLhv: 0.21,
      recoverableJacketWaterFractionOfFuelLhv: 0.23,
      internalRecuperationFractionOfFuelLhv: 0.08,
    },
    benchmarks: {
      capexPerKwUsd: 3600,
      fixedOmPerKwYearUsd: 55,
      ltsaPerKwYearUsd: 140,
      variableOmPerMwhUsd: 5.0,
      assumedBenchmarkDeliveredFuelUsdPerMmbtu: 5.0,
      designLifeYears: 20,
      availabilityPct: 94,
      capacityFactorPct: 94,
      capacityCreditPct: 94,
      constructionMonths: 15,
      riskPremiumBps: 100,
      tCo2ePerMwh: 0.47,
      decommPctOfCapex: 0.04,
      contingencyPct: 10,
      landAcresPerNameplateMw: 0.28,
    },
    provenance: {
      parameterKey: 'hyaxiom-purecell-400-ng',
      originAuthority: 'oem-published',
      verificationMethod: 'unverified-claim',
      siteApplicability: 'sku-specific-iso-rating',
      citation: {
        documentIdOrUrl: 'https://hyaxiom.com/wp-content/uploads/2025/11/hyaxiom-purecell-model-400-ng-data-sheet.pdf',
        title: 'HyAxiom PureCell Model 400 NG Datasheet',
        publicationOrRevisionDate: '2025-11',
        pageOrSection: 'Technical Specifications, p. 2',
        exactClaimExcerpt: 'Electrical output: 460 kW net AC; First-year average electrical efficiency: 42% LHV; Inlet gas pressure: 10-14 in. w.c.; No water consumption up to 30 °C (86 °F) and no normal water discharge.',
        unitsAndConditions: '460 kW net AC, first-year average LHV basis, 10-14 in. w.c. natural gas inlet.',
      },
      conflictStatus: 'active-authoritative',
    },
  },
  'stationary-pem-h2-benchmark': {
    packageId: 'stationary-pem-h2-benchmark',
    label: 'Stationary PEM Fuel Cell (ISO 14687:2025 Grade E Hydrogen)',
    manufacturer: 'Generic Stationary PEM Benchmark',
    model: '1.0 MW Containerized PEM Block',
    technologyId: 'fuel-cell-pem',
    chemistry: 'pem',
    primaryFuel: 'hydrogen',
    moduleNetAcKw: 1000,
    ratingBoundary: {
      powerBoundary: 'net-ac-skid-boundary',
      efficiencyAveragingBasis: 'bol-new-and-clean',
      outputGuaranteeMode: 'degrading-net-ac-over-time',
      includedAuxiliaries: ['skid-blowers'],
      isoReferenceConditions: { ambientTempC: 25, elevationM: 0, relativeHumidityPct: 50 },
      claimStatus: 'model-assumption',
    },
    electricalEfficiencyFractionLhv: 0.48,
    minInletPressure: { value: 50, unit: 'psig' },
    maxInletPressure: { value: 115, unit: 'psig' },
    degradation: {
      degradationDriver: 'operating-hours',
      outputDeclineRate: { pctPer1000Hours: 0.2, pctPerYear: 1.5, pctPerStartCycle: 0.005 },
      heatRateGrowthRate: { pctPer1000Hours: 0.2, pctPerYear: 1.5 },
      stackReplacementIntervalHours: 40000,
      stackReplacementIntervalYears: 6,
    },
    waterBalanceProfile: {
      zeroConsumptionBelowAmbientC: null,
      normalOperationalWaterDischargeGalPerMwh: 45,
      makeupWaterAboveThresholdGalPerMwh: 12,
      notes: 'Electrochemical H2 oxidation produces pure product water; closed-loop glycol/radiator cooling with DI water polishing.',
    },
    thermalDeliveryProfile: {
      internalStackTempRangeC: [60, 80],
      usableExhaustFlangeTempC: null,
      usableJacketWaterTempC: 60,
      recoverableExhaustFractionOfFuelLhv: 0,
      recoverableJacketWaterFractionOfFuelLhv: 0.38,
      internalRecuperationFractionOfFuelLhv: 0.04,
    },
    benchmarks: {
      capexPerKwUsd: 2900,
      fixedOmPerKwYearUsd: 40,
      ltsaPerKwYearUsd: 130,
      variableOmPerMwhUsd: 4.0,
      assumedBenchmarkDeliveredFuelUsdPerMmbtu: 22.0, // delivered high-purity H2 ($/MMBtu HHV equivalent)
      designLifeYears: 20,
      availabilityPct: 95,
      capacityFactorPct: 92,
      capacityCreditPct: 92,
      constructionMonths: 14,
      riskPremiumBps: 125,
      tCo2ePerMwh: 0,
      decommPctOfCapex: 0.04,
      contingencyPct: 12,
      landAcresPerNameplateMw: 0.20,
    },
    provenance: {
      parameterKey: 'stationary-pem-h2-benchmark',
      originAuthority: 'government-handbook',
      verificationMethod: 'screening-benchmark',
      siteApplicability: 'generic-reference',
      citation: {
        documentIdOrUrl: 'https://www.netl.doe.gov/sites/default/files/netl-file/FCHandbook7.pdf',
        title: 'NETL Fuel Cell Handbook (7th Edition) & ISO 14687:2025 Grade E Stationary Spec',
        publicationOrRevisionDate: '2004-11 / 2025-01',
        pageOrSection: 'PEMFC System Characterization & ISO 14687:2025 Grade E',
        exactClaimExcerpt: 'Stationary PEMFC systems operate at 60-80 °C on high-purity hydrogen (ISO 14687:2025 Grade E stationary / Grade D PEM) with ~45-50% LHV electrical efficiency.',
        unitsAndConditions: 'Net AC skid boundary, BOL LHV efficiency 48%, ISO 14687:2025 Grade E hydrogen.',
      },
      conflictStatus: 'active-authoritative',
    },
  },
  'mcfc-chp-ng-benchmark': {
    packageId: 'mcfc-chp-ng-benchmark',
    label: 'Stationary MCFC Fuel Cell (Natural Gas CHP)',
    manufacturer: 'Generic MCFC Benchmark (US EPA March 2015 CHP Catalog)',
    model: '1.4 MW–2.8 MW Molten Carbonate Block',
    technologyId: 'fuel-cell-mcfc',
    chemistry: 'mcfc',
    primaryFuel: 'natural-gas',
    moduleNetAcKw: 1400,
    ratingBoundary: {
      powerBoundary: 'net-ac-skid-boundary',
      efficiencyAveragingBasis: 'bol-new-and-clean',
      outputGuaranteeMode: 'constant-net-ac-via-fuel-trim',
      includedAuxiliaries: ['skid-blowers', 'internal-reformer', 'desulfurization'],
      isoReferenceConditions: { ambientTempC: 15, elevationM: 0, relativeHumidityPct: 60 },
      claimStatus: 'model-assumption',
    },
    electricalEfficiencyFractionLhv: 0.47,
    minInletPressure: { value: 25, unit: 'psig' },
    maxInletPressure: { value: 45, unit: 'psig' },
    degradation: {
      degradationDriver: 'operating-hours',
      outputDeclineRate: { pctPer1000Hours: 0 },
      heatRateGrowthRate: { pctPer1000Hours: 0.14, pctPerYear: 1.1 },
      stackReplacementIntervalHours: 61320,
      stackReplacementIntervalYears: 7,
    },
    waterBalanceProfile: {
      zeroConsumptionBelowAmbientC: null,
      normalOperationalWaterDischargeGalPerMwh: 95,
      makeupWaterAboveThresholdGalPerMwh: 210,
      notes: 'Requires continuous makeup water treatment and blowdown discharge per US EPA Catalog of CHP Technologies Section 6 (March 2015).',
    },
    thermalDeliveryProfile: {
      internalStackTempRangeC: [600, 650],
      usableExhaustFlangeTempC: 370,
      usableJacketWaterTempC: null,
      recoverableExhaustFractionOfFuelLhv: 0.34,
      recoverableJacketWaterFractionOfFuelLhv: 0,
      internalRecuperationFractionOfFuelLhv: 0.12,
    },
    benchmarks: {
      capexPerKwUsd: 4200,
      fixedOmPerKwYearUsd: 60,
      ltsaPerKwYearUsd: 190,
      variableOmPerMwhUsd: 5.5,
      assumedBenchmarkDeliveredFuelUsdPerMmbtu: 5.0,
      designLifeYears: 20,
      availabilityPct: 92,
      capacityFactorPct: 92,
      capacityCreditPct: 92,
      constructionMonths: 18,
      riskPremiumBps: 125,
      tCo2ePerMwh: 0.42,
      decommPctOfCapex: 0.05,
      contingencyPct: 12,
      landAcresPerNameplateMw: 0.32,
    },
    provenance: {
      parameterKey: 'mcfc-chp-ng-benchmark',
      originAuthority: 'government-handbook',
      verificationMethod: 'screening-benchmark',
      siteApplicability: 'generic-reference',
      citation: {
        documentIdOrUrl: 'https://www.epa.gov/sites/default/files/2015-07/documents/catalog_of_chp_technologies_section_6._technology_characterization_-_fuel_cells.pdf',
        title: 'US EPA Catalog of CHP Technologies — Section 6: Technology Characterization — Fuel Cells',
        publicationOrRevisionDate: '2015-03',
        pageOrSection: 'Section 6, Table 6-2 & Table 6-3 (March 2015)',
        exactClaimExcerpt: 'MCFC 1,400 kW system: ~47% LHV (~42.5% HHV) electrical efficiency, ~650 °C operating temperature, high-grade ~370 °C exhaust heat.',
        unitsAndConditions: 'Net AC skid boundary, March 2015 US EPA CHP Catalog reference.',
      },
      conflictStatus: 'active-authoritative',
    },
  },
};

export const DEFAULT_PACKAGE_BY_TECHNOLOGY_ID: Readonly<
  Record<'fuel-cell-sofc' | 'fuel-cell-pafc' | 'fuel-cell-pem' | 'fuel-cell-mcfc', CuratedFuelCellPackageSpec>
> = {
  'fuel-cell-sofc': CURATED_FUEL_CELL_PACKAGES['bloom-energy-server-6.5-ng'],
  'fuel-cell-pafc': CURATED_FUEL_CELL_PACKAGES['hyaxiom-purecell-400-ng'],
  'fuel-cell-pem': CURATED_FUEL_CELL_PACKAGES['stationary-pem-h2-benchmark'],
  'fuel-cell-mcfc': CURATED_FUEL_CELL_PACKAGES['mcfc-chp-ng-benchmark'],
};

/**
 * Dimensional heat-rate conversion (Finding 11):
 *   HeatRate_HHV (MMBtu/MWh_netAC) = (3.412 / eta_LHV_fraction) * (HHV / LHV)_fuel
 *
 * Strictly validates `efficiencyFractionLhv` in (0, 1) — throws `RangeError` if a
 * percentage (e.g. 50 instead of 0.50) is passed.
 */
export function computeFuelCellNetAcHeatRateHhv(args: {
  efficiencyFractionLhv: number;
  primaryFuel: 'natural-gas' | 'rng-biogas' | 'hydrogen' | 'hydrogen-ng-blend';
  suppliedHeatRateHhvBtuPerKwh?: number;
}): {
  hhvToLhvRatio: number;
  heatRateHhvMmbtuPerMwh: number;
  heatRateHhvBtuPerKwh: number;
} {
  const { efficiencyFractionLhv, primaryFuel, suppliedHeatRateHhvBtuPerKwh } = args;
  if (
    !Number.isFinite(efficiencyFractionLhv) ||
    efficiencyFractionLhv <= 0 ||
    efficiencyFractionLhv >= 1
  ) {
    throw new RangeError(
      `computeFuelCellNetAcHeatRateHhv: efficiencyFractionLhv must be a dimensionless fraction in (0, 1); got ${efficiencyFractionLhv}`,
    );
  }
  const hhvToLhvRatio = HHV_TO_LHV_RATIO_BY_FUEL[primaryFuel];
  if (!hhvToLhvRatio) {
    throw new RangeError(
      `computeFuelCellNetAcHeatRateHhv: unsupported primaryFuel '${primaryFuel}'`,
    );
  }

  if (
    typeof suppliedHeatRateHhvBtuPerKwh === 'number' &&
    Number.isFinite(suppliedHeatRateHhvBtuPerKwh)
  ) {
    if (suppliedHeatRateHhvBtuPerKwh < 2500 || suppliedHeatRateHhvBtuPerKwh > 25000) {
      throw new RangeError(
        `computeFuelCellNetAcHeatRateHhv: suppliedHeatRateHhvBtuPerKwh must be within physical envelope [2500, 25000] Btu/kWh; got ${suppliedHeatRateHhvBtuPerKwh}`,
      );
    }
    return {
      hhvToLhvRatio,
      heatRateHhvBtuPerKwh: suppliedHeatRateHhvBtuPerKwh,
      heatRateHhvMmbtuPerMwh: suppliedHeatRateHhvBtuPerKwh / 1000,
    };
  }

  const heatRateHhvMmbtuPerMwh = (3.412 / efficiencyFractionLhv) * hhvToLhvRatio;
  return {
    hhvToLhvRatio,
    heatRateHhvMmbtuPerMwh,
    heatRateHhvBtuPerKwh: heatRateHhvMmbtuPerMwh * 1000,
  };
}

/**
 * Resolve net deliverable AC ratio at PCC without double-derating vendor net-AC
 * ratings (Finding 8 / Negative Test 2).
 *
 * When `ratingBoundary.powerBoundary === 'net-ac-skid-boundary'`, any auxiliary
 * already in `ratingBoundary.includedAuxiliaries` (e.g., `'skid-blowers'`,
 * `'internal-reformer'`, `'desulfurization'`) is ALREADY deducted inside the
 * vendor's published net-AC rating and MUST NOT be subtracted a second time.
 * Similarly, when `efficiencyAveragingBasis === 'cumulative-contract-term'` or
 * `outputGuaranteeMode === 'constant-net-ac-via-fuel-trim'`, output degradation
 * is already accounted for in the cumulative/fuel-trimmed guarantee and is never
 * subtracted from net AC capacity.
 */
export function resolveNetDeliverableAcAtPcc(args: {
  ratingBoundary: RatingBoundarySpec;
  requestedDeductions?: Partial<Record<FuelCellAuxiliaryLoadKey, number>>;
  requestedBolToCumulativeDerateFraction?: number;
}): {
  netDeliverableAcRatioAtPcc: number;
  externalParasiticDerateFraction: number;
  skippedIncludedAuxiliaries: FuelCellAuxiliaryLoadKey[];
  skippedCumulativeDegradationDerate: boolean;
} {
  const { ratingBoundary, requestedDeductions = {}, requestedBolToCumulativeDerateFraction = 0 } = args;
  const included = new Set<FuelCellAuxiliaryLoadKey>(ratingBoundary.includedAuxiliaries);
  const skippedIncludedAuxiliaries: FuelCellAuxiliaryLoadKey[] = [];
  let externalParasiticDerateFraction = 0;

  if (ratingBoundary.powerBoundary === 'net-ac-pcc') {
    return {
      netDeliverableAcRatioAtPcc: 1,
      externalParasiticDerateFraction: 0,
      skippedIncludedAuxiliaries: Object.keys(requestedDeductions) as FuelCellAuxiliaryLoadKey[],
      skippedCumulativeDegradationDerate: requestedBolToCumulativeDerateFraction > 0,
    };
  }

  for (const [auxKey, rawFraction] of Object.entries(requestedDeductions) as Array<
    [FuelCellAuxiliaryLoadKey, number | undefined]
  >) {
    if (typeof rawFraction !== 'number' || !Number.isFinite(rawFraction) || rawFraction <= 0) {
      continue;
    }
    if (
      (ratingBoundary.powerBoundary === 'net-ac-skid-boundary' ||
        ratingBoundary.powerBoundary === 'inverter-ac-terminals') &&
      included.has(auxKey)
    ) {
      skippedIncludedAuxiliaries.push(auxKey);
      continue;
    }
    externalParasiticDerateFraction += rawFraction;
  }

  let skippedCumulativeDegradationDerate = false;
  if (requestedBolToCumulativeDerateFraction > 0) {
    if (
      ratingBoundary.efficiencyAveragingBasis === 'cumulative-contract-term' ||
      ratingBoundary.outputGuaranteeMode === 'constant-net-ac-via-fuel-trim'
    ) {
      skippedCumulativeDegradationDerate = true;
    } else {
      externalParasiticDerateFraction += requestedBolToCumulativeDerateFraction;
    }
  }

  const netDeliverableAcRatioAtPcc = Math.max(0, 1 - externalParasiticDerateFraction);
  return {
    netDeliverableAcRatioAtPcc,
    externalParasiticDerateFraction,
    skippedIncludedAuxiliaries,
    skippedCumulativeDegradationDerate,
  };
}

/**
 * Mutual exclusion guard for LTSA vs. discrete stack replacement events
 * (Finding 7 / Negative Test 9).
 */
export function validateStackCostAccounting(args: {
  stackCostAccountingMode: 'ltsa-levelized-opex' | 'discrete-refurbishment-events';
  ltsaPerKwYearUsd: number;
  discreteRefurbishmentEvents?: ReadonlyArray<{
    yearFromCod: number;
    usdPerKw: number;
    description: string;
  }>;
  capacityMw: number;
  strict?: boolean;
}): {
  effectiveFixedOmAddonPerKwYearUsd: number;
  effectiveRefurbishmentEvents: RefurbishmentEvent[];
} {
  const {
    stackCostAccountingMode,
    ltsaPerKwYearUsd,
    discreteRefurbishmentEvents = [],
    capacityMw,
    strict = true,
  } = args;

  if (
    stackCostAccountingMode === 'ltsa-levelized-opex' &&
    discreteRefurbishmentEvents.length > 0
  ) {
    if (strict) {
      throw new RangeError(
        'validateStackCostAccounting: stackCostAccountingMode is "ltsa-levelized-opex" but discreteRefurbishmentEvents is non-empty — stack replacement cannot be double-charged in both LTSA opex and discrete refurbishment capex.',
      );
    }
    return {
      effectiveFixedOmAddonPerKwYearUsd: ltsaPerKwYearUsd,
      effectiveRefurbishmentEvents: [],
    };
  }

  if (stackCostAccountingMode === 'ltsa-levelized-opex') {
    return {
      effectiveFixedOmAddonPerKwYearUsd: ltsaPerKwYearUsd,
      effectiveRefurbishmentEvents: [],
    };
  }

  const kw = capacityMw * KW_PER_MW;
  return {
    effectiveFixedOmAddonPerKwYearUsd: 0,
    effectiveRefurbishmentEvents: discreteRefurbishmentEvents.map((ev) => ({
      yearFromCod: ev.yearFromCod,
      usd: ev.usdPerKw * kw,
      description: ev.description,
    })),
  };
}

export interface ClosedThermalAndWaterBalanceResult {
  fuelInputThermalMwLhv: number;
  netAcElectricalMw: number;
  internalRecuperationMw: number;
  auxHeatLossMw: number;
  recoverableChpExhaustMw: number;
  recoverableChpJacketWaterMw: number;
  totalRecoverableThermalMw: number;
  utilizedThermalHeatingOrSalesMw: number;
  utilizedAbsorptionChillerInputMw: number;
  absorptionChillerCoolingEffectMw: number;
  absorptionChillerHeatRejectionMw: number;
  unutilizedRejectionToAmbientMw: number;
  energyBalanceResidualMw: number;
  waterConsumptionGalPerDay: number;
  normalOperationalWaterDischargeGalPerDay: number;
  exceedsMunicipalOrdinanceCap: boolean;
}

/**
 * Closed thermal and water balance with No Double-Credit Guard (Finding 12).
 * Rejects simultaneous allocation of the same recoverable thermal MW stream to
 * both thermal heating/sales and absorption chilling.
 */
export function computeClosedThermalAndWaterBalance(args: {
  pkg: CuratedFuelCellPackageSpec;
  netAcElectricalMw: number;
  ambientTempC?: number;
  allocatedToHeatingOrSalesMw?: number;
  allocatedToAbsorptionChillerMw?: number;
  absorptionChillerCopThermal?: number;
  municipalWaterOrdinanceCapGalPerDay?: number;
}): ClosedThermalAndWaterBalanceResult {
  const {
    pkg,
    netAcElectricalMw,
    ambientTempC = 25,
    allocatedToHeatingOrSalesMw = 0,
    allocatedToAbsorptionChillerMw = 0,
    absorptionChillerCopThermal = 0.72,
    municipalWaterOrdinanceCapGalPerDay,
  } = args;

  const eta = pkg.electricalEfficiencyFractionLhv;
  const fuelInputThermalMwLhv = netAcElectricalMw / eta;
  const internalRecuperationMw =
    fuelInputThermalMwLhv * pkg.thermalDeliveryProfile.internalRecuperationFractionOfFuelLhv;
  const recoverableChpExhaustMw =
    fuelInputThermalMwLhv * pkg.thermalDeliveryProfile.recoverableExhaustFractionOfFuelLhv;
  const recoverableChpJacketWaterMw =
    fuelInputThermalMwLhv * pkg.thermalDeliveryProfile.recoverableJacketWaterFractionOfFuelLhv;
  const totalRecoverableThermalMw = recoverableChpExhaustMw + recoverableChpJacketWaterMw;

  if (
    allocatedToHeatingOrSalesMw + allocatedToAbsorptionChillerMw >
    totalRecoverableThermalMw + 1e-6
  ) {
    throw new RangeError(
      `computeClosedThermalAndWaterBalance: allocated thermal demand (${(
        allocatedToHeatingOrSalesMw + allocatedToAbsorptionChillerMw
      ).toFixed(2)} MWth) exceeds total recoverable CHP heat (${totalRecoverableThermalMw.toFixed(
        2,
      )} MWth) — cannot double-credit the same waste-heat stream to both heating and absorption chilling.`,
    );
  }

  const auxHeatLossMw = Math.max(
    0,
    fuelInputThermalMwLhv -
      netAcElectricalMw -
      internalRecuperationMw -
      totalRecoverableThermalMw,
  );
  const unutilizedRejectionToAmbientMw = Math.max(
    0,
    totalRecoverableThermalMw - allocatedToHeatingOrSalesMw - allocatedToAbsorptionChillerMw,
  );
  const absorptionChillerCoolingEffectMw =
    allocatedToAbsorptionChillerMw * absorptionChillerCopThermal;
  // First-law closure on the absorption chiller loop: Q_absorber+condenser = Q_in + Q_cooling
  const absorptionChillerHeatRejectionMw =
    allocatedToAbsorptionChillerMw + absorptionChillerCoolingEffectMw;

  const energyBalanceResidualMw =
    fuelInputThermalMwLhv -
    (netAcElectricalMw +
      internalRecuperationMw +
      auxHeatLossMw +
      allocatedToHeatingOrSalesMw +
      allocatedToAbsorptionChillerMw +
      unutilizedRejectionToAmbientMw);

  const wb = pkg.waterBalanceProfile;
  const isBelowZeroWaterThreshold =
    wb.zeroConsumptionBelowAmbientC != null && ambientTempC <= wb.zeroConsumptionBelowAmbientC;
  const galPerMwh = isBelowZeroWaterThreshold ? 0 : wb.makeupWaterAboveThresholdGalPerMwh;
  const dailyMwh = netAcElectricalMw * 24;
  const waterConsumptionGalPerDay = dailyMwh * galPerMwh;
  const normalOperationalWaterDischargeGalPerDay =
    dailyMwh * wb.normalOperationalWaterDischargeGalPerMwh;

  const exceedsMunicipalOrdinanceCap =
    typeof municipalWaterOrdinanceCapGalPerDay === 'number' &&
    municipalWaterOrdinanceCapGalPerDay > 0 &&
    waterConsumptionGalPerDay > municipalWaterOrdinanceCapGalPerDay;

  return {
    fuelInputThermalMwLhv,
    netAcElectricalMw,
    internalRecuperationMw,
    auxHeatLossMw,
    recoverableChpExhaustMw,
    recoverableChpJacketWaterMw,
    totalRecoverableThermalMw,
    utilizedThermalHeatingOrSalesMw: allocatedToHeatingOrSalesMw,
    utilizedAbsorptionChillerInputMw: allocatedToAbsorptionChillerMw,
    absorptionChillerCoolingEffectMw,
    absorptionChillerHeatRejectionMw,
    unutilizedRejectionToAmbientMw,
    energyBalanceResidualMw,
    waterConsumptionGalPerDay,
    normalOperationalWaterDischargeGalPerDay,
    exceedsMunicipalOrdinanceCap,
  };
}

/**
 * Evaluate island-readiness & transient step-load envelope for a stationary
 * fuel-cell phase (Finding 13 / Negative Test 6).
 */
export function evaluateFuelCellIslandReadiness(args: {
  gridConnection: 'islanded' | 'grid-connected' | 'undecided';
  hasBatteryInMix: boolean;
  config?: FuelCellPhaseConfig;
}): {
  islandReady: boolean;
  reasons: string[];
} {
  const { gridConnection, hasBatteryInMix, config } = args;
  if (gridConnection !== 'islanded') {
    return { islandReady: true, reasons: [] };
  }

  const reasons: string[] = [];
  const ir = config?.islandReadiness;

  if (!hasBatteryInMix) {
    reasons.push(
      'Islanded fuel-cell operation requires a paired energy-storage transient buffer (BESS) for step-load ramping.',
    );
  }
  if (!ir?.gridFormingInverterVerified) {
    reasons.push(
      'Grid-forming inverter controls (GFM) are not verified; grid-following inverters cannot establish island voltage/frequency reference.',
    );
  }
  if (!ir?.blackStartAuxiliaryVerified) {
    reasons.push(
      'Black-start auxiliary power sequence to heat fuel-cell reformers/blowers to operating temperature is not verified.',
    );
  }
  const reqStep = ir?.requiredStepLoadMw ?? 0;
  const rampRate = ir?.hotStandbyRampRateMwPerMin ?? 0;
  const bessBufferMw = ir?.pairedBessStepBufferMw ?? 0;
  const bessUsableMwh = ir?.pairedBessUsableMwh ?? 0;
  const minSoc = ir?.pairedBessMinReservedSocPct ?? 0;
  if (reqStep > 0 && bessBufferMw + rampRate < reqStep) {
    reasons.push(
      `Transient step-load deficit: required step ${reqStep} MW exceeds BESS buffer (${bessBufferMw} MW) + fuel-cell ramp (${rampRate} MW/min).`,
    );
  }
  if (bessUsableMwh <= 0 || minSoc <= 0) {
    reasons.push(
      'Depleted or unreserved BESS state-of-charge (usable MWh / minimum reserved SOC) cannot bridge thermal reformer lag during an AI/IT step load.',
    );
  }
  if (!ir?.singleGasMainCommonModeMitigated) {
    reasons.push(
      'Single unlooped gas main / regulator common-mode failure is not mitigated for Tier III/IV island resilience.',
    );
  }

  return {
    islandReady: reasons.length === 0,
    reasons,
  };
}

/**
 * Multi-cohort aging calculator (Finding 7 / Negative Test 10).
 * Each cohort tracks stack operating hours and heat-rate growth from its own
 * installation year (`cohortInstallYear`), never from Cohort 1's Year-0 COD.
 */
export function computeCohortAgedHeatRateMultiplier(args: {
  evaluationYear: number;
  cohortInstallYear: number;
  degradation: FuelCellDegradationSpec;
  operatingHoursPerYear?: number;
}): {
  cohortOperatingYears: number;
  cohortOperatingHours: number;
  heatRateMultiplier: number;
  stackReplacementCount: number;
} {
  const {
    evaluationYear,
    cohortInstallYear,
    degradation,
    operatingHoursPerYear = 8322, // 95% CF * 8760
  } = args;

  if (evaluationYear <= cohortInstallYear) {
    return {
      cohortOperatingYears: 0,
      cohortOperatingHours: 0,
      heatRateMultiplier: 1,
      stackReplacementCount: 0,
    };
  }

  const cohortOperatingYears = evaluationYear - cohortInstallYear;
  const totalCohortHours = cohortOperatingYears * operatingHoursPerYear;
  const intervalHours = degradation.stackReplacementIntervalHours ?? 0;
  const stackReplacementCount =
    intervalHours > 0 ? Math.floor(totalCohortHours / intervalHours) : 0;
  const hoursSinceLastStackReset =
    intervalHours > 0 ? totalCohortHours % intervalHours : totalCohortHours;

  let growthPct = 0;
  if (
    degradation.degradationDriver === 'operating-hours' &&
    degradation.heatRateGrowthRate?.pctPer1000Hours != null
  ) {
    growthPct = (hoursSinceLastStackReset / 1000) * degradation.heatRateGrowthRate.pctPer1000Hours;
  } else if (degradation.heatRateGrowthRate?.pctPerYear != null) {
    const yearsSinceReset =
      degradation.stackReplacementIntervalYears && degradation.stackReplacementIntervalYears > 0
        ? cohortOperatingYears % degradation.stackReplacementIntervalYears
        : cohortOperatingYears;
    growthPct = yearsSinceReset * degradation.heatRateGrowthRate.pctPerYear;
  }

  return {
    cohortOperatingYears,
    cohortOperatingHours: hoursSinceLastStackReset,
    heatRateMultiplier: 1 + growthPct / 100,
    stackReplacementCount,
  };
}

/**
 * Append a provenance record to a fuel-supply provenance ledger while retaining
 * contradictory or superseded claims explicitly (Finding 16 / Negative Test 8).
 */
export function appendToFuelSupplyProvenanceLedger(
  existingLedger: readonly FuelCellParameterProvenance[] | undefined,
  incoming: FuelCellParameterProvenance,
): readonly FuelCellParameterProvenance[] {
  const ledger = [...(existingLedger ?? [])];
  const key = incoming.parameterKey ?? incoming.citation.title;

  const updated = ledger.map((entry) => {
    const entryKey = entry.parameterKey ?? entry.citation.title;
    if (entryKey !== key) return entry;
    if (incoming.supersedesDocumentIdOrUrl === entry.citation.documentIdOrUrl) {
      return { ...entry, conflictStatus: 'superseded-retained' as const };
    }
    if (
      entry.recordedValueSummary &&
      incoming.recordedValueSummary &&
      entry.recordedValueSummary !== incoming.recordedValueSummary
    ) {
      return { ...entry, conflictStatus: 'contradictory-retained' as const };
    }
    return entry;
  });

  const hasConflict = updated.some(
    (e) =>
      (e.parameterKey ?? e.citation.title) === key &&
      e.conflictStatus === 'contradictory-retained',
  );

  updated.push({
    ...incoming,
    conflictStatus: hasConflict
      ? 'contradictory-retained'
      : incoming.conflictStatus ?? 'active-authoritative',
  });

  return updated;
}

/**
 * Evaluate whether a parameter provenance entry satisfies an independent or
 * site-contracted verification gate (Finding 16 / Negative Test 5).
 * A site-uploaded vendor brochure/quote (`originAuthority: 'supplier-quote'` or
 * `'oem-published'` with `verificationMethod: 'unverified-claim'`) NEVER
 * satisfies an `'independent-test'` or `'utility-or-ahj'` gate.
 */
export function satisfiesProvenanceGate(
  provenance: FuelCellParameterProvenance | undefined,
  requiredGate: 'independent-test' | 'utility-or-ahj' | 'site-contracted',
): boolean {
  if (!provenance) return false;
  if (provenance.verificationMethod === 'unverified-claim') return false;
  if (requiredGate === 'independent-test') {
    return (
      provenance.originAuthority === 'independent-test-lab' &&
      provenance.verificationMethod === 'third-party-certified'
    );
  }
  if (requiredGate === 'utility-or-ahj') {
    return (
      provenance.originAuthority === 'utility-or-ahj' &&
      provenance.verificationMethod === 'site-measured-or-contracted'
    );
  }
  return provenance.verificationMethod === 'site-measured-or-contracted';
}

/**
 * Build an immutable `ResolvedFuelCellEconomicSnapshot` for a given fuel-cell
 * technology ID, optional `FuelCellPhaseConfig`, and optional `FuelSupplyEvidence`
 * from the SED (Findings 4, 5, 7, 8, 11, 17).
 */
export function buildFuelCellEconomicSnapshot(args: {
  technologyId:
    | 'fuel-cell-sofc'
    | 'fuel-cell-pafc'
    | 'fuel-cell-pem'
    | 'fuel-cell-mcfc'
    | 'fuel-cell-unspecified';
  config?: FuelCellPhaseConfig;
  fuelSupply?: FuelSupplyEvidence | null;
  gridConnection?: 'islanded' | 'grid-connected' | 'undecided';
  hasBatteryInMix?: boolean;
  explicitCapexPerKwUsd?: number | null;
  explicitDeliveredTariffUsdPerMmbtu?: number | null;
}): ResolvedFuelCellEconomicSnapshot {
  const {
    technologyId,
    config,
    fuelSupply,
    gridConnection = 'grid-connected',
    hasBatteryInMix = false,
    explicitCapexPerKwUsd,
    explicitDeliveredTariffUsdPerMmbtu,
  } = args;

  const holds: PathwayRelianceHold[] = [];

  if (
    technologyId === 'fuel-cell-unspecified' ||
    config?.chemistry === 'unspecified' ||
    config?.primaryFuel === 'unspecified'
  ) {
    holds.push({
      id: 'hold-fuel-cell-unresolved-chemistry',
      domain: 'unresolved-chemistry-or-fuel',
      severity: 'blocking-hold',
      summary:
        'Stationary fuel-cell chemistry or primary fuel is unspecified — configuration remains unresolved and cannot default to SOFC.',
      remediationNextStep:
        'Select an explicit fuel-cell chemistry (SOFC, PAFC, PEMFC, or MCFC) and primary fuel, or use Compare All Suitable.',
    });
    return {
      configId: 'fuel-cell-unspecified',
      chemistry: 'unspecified',
      primaryFuel: config?.primaryFuel ?? 'unspecified',
      oemPackageId: null,
      oemPackageLabel: 'Unresolved Fuel-Cell Configuration',
      powerBoundary: 'net-ac-skid-boundary',
      efficiencyAveragingBasis: 'cumulative-contract-term',
      outputGuaranteeMode: 'constant-net-ac-via-fuel-trim',
      electricalEfficiencyFractionLhv: null,
      hhvToLhvRatio: null,
      netAcHeatRateHhvBtuPerKwh: null,
      netAcHeatRateHhvMmbtuPerMwh: null,
      externalParasiticDerateFraction: 0,
      netDeliverableAcRatioAtPcc: 0,
      capexPerKwUsd: null,
      fixedOmPerKwYearUsd: null,
      variableOmPerMwhUsd: null,
      deliveredFuelTariffUsdPerMmbtu: null,
      fuelPriceBasis: 'unavailable',
      fuelCostPerMwhUsd: null,
      stackCostAccountingMode: 'ltsa-levelized-opex',
      ltsaPerKwYearUsd: 0,
      discreteRefurbishmentEvents: [],
      capacityFactorPct: 0,
      capacityCreditPct: 0,
      tCo2ePerMwh: 0,
      landAcresPerNameplateMw: 0,
      economicQualificationPosture: 'unsupported-incomplete',
      relianceHolds: holds,
    };
  }

  const pkg =
    (config?.oemPackageId ? CURATED_FUEL_CELL_PACKAGES[config.oemPackageId] : undefined) ??
    DEFAULT_PACKAGE_BY_TECHNOLOGY_ID[technologyId];

  const primaryFuel: 'natural-gas' | 'rng-biogas' | 'hydrogen' | 'hydrogen-ng-blend' =
    config?.primaryFuel && (config.primaryFuel as string) !== 'unspecified'
      ? (config.primaryFuel as 'natural-gas' | 'rng-biogas' | 'hydrogen' | 'hydrogen-ng-blend')
      : pkg.primaryFuel;
  const effLhv = config?.electricalEfficiencyFractionLhv ?? pkg.electricalEfficiencyFractionLhv;
  const heatRate = computeFuelCellNetAcHeatRateHhv({
    efficiencyFractionLhv: effLhv,
    primaryFuel,
    suppliedHeatRateHhvBtuPerKwh:
      config?.suppliedHeatRateHhvBtuPerKwh ?? pkg.suppliedHeatRateHhvBtuPerKwh,
  });

  const ratingBoundary = config?.ratingBoundary ?? pkg.ratingBoundary;
  const ext = config?.externalParasitics;
  const pcc = resolveNetDeliverableAcAtPcc({
    ratingBoundary,
    requestedDeductions: {
      ...(ext?.boosterCompressorFraction
        ? { 'fuel-booster-compressor': ext.boosterCompressorFraction }
        : {}),
      ...(ext?.mvStepUpTransformerLossFraction
        ? { 'step-up-transformer': ext.mvStepUpTransformerLossFraction }
        : {}),
      ...(ext?.externalDryCoolerFraction
        ? { 'dry-cooler-fans': ext.externalDryCoolerFraction }
        : {}),
    },
  });

  // Check site fuel-supply evidence holds (Athena & general site readiness)
  const pressureVerified =
    fuelSupply?.deliveryPressure?.verificationStatus === 'verified' &&
    fuelSupply.deliveryPressure.minGuaranteedPsigOrInWc != null;
  if (!pressureVerified) {
    holds.push({
      id: `hold-${technologyId}-delivery-pressure`,
      domain: 'fuel-delivery-pressure',
      severity: 'blocking-hold',
      summary: `Minimum guaranteed gas/fuel inlet pressure (${pkg.minInletPressure.value} ${pkg.minInletPressure.unit}) is unverified at the site boundary.`,
      remediationNextStep: `Obtain an LDC/pipeline pressure letter confirming minimum delivery pressure >= ${pkg.minInletPressure.value} ${pkg.minInletPressure.unit} or size an external booster compressor.`,
    });
  }

  const firmTransportVerified =
    fuelSupply?.contractedTransport?.verificationStatus === 'verified' &&
    fuelSupply.contractedTransport.serviceType === 'firm-transportation';
  if (!firmTransportVerified) {
    holds.push({
      id: `hold-${technologyId}-firm-transport`,
      domain: 'fuel-firm-transport',
      severity: 'blocking-hold',
      summary:
        'Firm gas/hydrogen transportation capacity and winter curtailment priority are unverified (state-border corridor context or electrical utility notes do not establish gas deliverability).',
      remediationNextStep:
        'Secure a binding LDC/pipeline firm transportation capacity determination (Dth/day) and interconnect lateral study.',
    });
  }

  const qualityVerified =
    primaryFuel === 'hydrogen'
      ? fuelSupply?.hydrogenSupply?.verificationStatus === 'verified' &&
        fuelSupply.hydrogenSupply.purityStandard != null
      : fuelSupply?.gasQualityProfile?.verificationStatus === 'verified';
  if (!qualityVerified) {
    holds.push({
      id: `hold-${technologyId}-fuel-quality`,
      domain: primaryFuel === 'hydrogen' ? 'hydrogen-purity-iso14687' : 'fuel-quality-sulfur-siloxane',
      severity: 'blocking-hold',
      summary:
        primaryFuel === 'hydrogen'
          ? 'Hydrogen purity specification (ISO 14687:2025 Grade E stationary / Grade D PEM) and delivery logistics are unverified.'
          : 'Site gas constituent quality (HHV/LHV ratio, sulfur/odorant, and siloxane limits) is unverified.',
      remediationNextStep:
        primaryFuel === 'hydrogen'
          ? 'Verify hydrogen supply contract to ISO 14687:2025 Grade E / Grade D purity limits and NFPA 2 / OSHA 1910.103 storage compliance.'
          : 'Obtain LDC tariff gas-quality chromatograph analysis and size desulfurization/siloxane media beds.',
    });
  }

  const islandEval = evaluateFuelCellIslandReadiness({
    gridConnection,
    hasBatteryInMix,
    config,
  });
  if (!islandEval.islandReady) {
    holds.push({
      id: `hold-${technologyId}-island-transient`,
      domain: 'island-transient-envelope',
      severity: 'blocking-hold',
      summary: islandEval.reasons.join(' '),
      remediationNextStep:
        'Verify grid-forming BESS inverter controls, black-start auxiliary power, reserved SOC step-load buffer, and dual/looped gas supply.',
    });
  }

  // Resolve capex and delivered fuel tariff posture
  const capexPerKwUsd =
    explicitCapexPerKwUsd !== undefined ? explicitCapexPerKwUsd : pkg.benchmarks.capexPerKwUsd;

  let deliveredFuelTariffUsdPerMmbtu: number | null = null;
  let fuelPriceBasis: ResolvedFuelCellEconomicSnapshot['fuelPriceBasis'] = 'unavailable';

  if (explicitDeliveredTariffUsdPerMmbtu !== undefined) {
    deliveredFuelTariffUsdPerMmbtu = explicitDeliveredTariffUsdPerMmbtu;
    fuelPriceBasis =
      explicitDeliveredTariffUsdPerMmbtu != null && explicitDeliveredTariffUsdPerMmbtu > 0
        ? 'site-delivered-tariff'
        : 'unavailable';
  } else if (
    fuelSupply?.contractedTransport?.verificationStatus === 'verified' &&
    typeof fuelSupply.contractedTransport.deliveredTariffUsdPerMmbtu === 'number' &&
    fuelSupply.contractedTransport.deliveredTariffUsdPerMmbtu > 0
  ) {
    deliveredFuelTariffUsdPerMmbtu = fuelSupply.contractedTransport.deliveredTariffUsdPerMmbtu;
    fuelPriceBasis = 'site-delivered-tariff';
  } else if (
    primaryFuel !== 'hydrogen' &&
    fuelSupply &&
    (fuelSupply.priceSeries === 'citygate' || fuelSupply.priceSeries === 'industrial') &&
    typeof fuelSupply.naturalGasPriceUsdPerMmbtu === 'number' &&
    fuelSupply.naturalGasPriceUsdPerMmbtu > 0
  ) {
    deliveredFuelTariffUsdPerMmbtu = fuelSupply.naturalGasPriceUsdPerMmbtu;
    fuelPriceBasis = 'benchmark-assumption';
    holds.push({
      id: `hold-${technologyId}-tariff-assumption`,
      domain: 'delivered-fuel-tariff',
      severity: 'assumption-disclosure',
      summary: `Using regional EIA ${fuelSupply.priceSeries} price ($${fuelSupply.naturalGasPriceUsdPerMmbtu.toFixed(2)}/MMBtu) as a screening benchmark; site-specific LDC delivery tariff is not yet contracted.`,
      remediationNextStep: 'Replace with contracted LDC industrial/transportation tariff schedule.',
    });
  } else {
    // Henry Hub spot fallback or no site fuel supply -> benchmark assumption (never treated as verified site tariff!)
    deliveredFuelTariffUsdPerMmbtu = pkg.benchmarks.assumedBenchmarkDeliveredFuelUsdPerMmbtu;
    fuelPriceBasis =
      fuelSupply?.priceSeries === 'henry-hub-fallback'
        ? 'henry-hub-unverified'
        : 'benchmark-assumption';
    holds.push({
      id: `hold-${technologyId}-henry-hub-not-ldc-tariff`,
      domain: 'delivered-fuel-tariff',
      severity: 'assumption-disclosure',
      summary:
        fuelSupply?.priceSeries === 'henry-hub-fallback'
          ? `Wholesale Henry Hub benchmark ($${fuelSupply.naturalGasPriceUsdPerMmbtu?.toFixed(2) ?? '3.03'}/MMBtu) is not a site-delivered LDC tariff; screening uses the $${pkg.benchmarks.assumedBenchmarkDeliveredFuelUsdPerMmbtu.toFixed(2)}/MMBtu benchmark assumption.`
          : `No verified site-delivered fuel tariff; screening uses the $${pkg.benchmarks.assumedBenchmarkDeliveredFuelUsdPerMmbtu.toFixed(2)}/MMBtu benchmark assumption.`,
      remediationNextStep:
        'Obtain a site-delivered LDC rate schedule and firm transportation tariff.',
    });
  }

  // If caller explicitly nulled out tariff or capex (Negative Test 4: missing tariff or capex)
  const isMissingCriticalEconomics =
    capexPerKwUsd == null ||
    capexPerKwUsd <= 0 ||
    deliveredFuelTariffUsdPerMmbtu == null ||
    deliveredFuelTariffUsdPerMmbtu <= 0;

  const fuelCostPerMwhUsd =
    !isMissingCriticalEconomics && deliveredFuelTariffUsdPerMmbtu != null
      ? deliveredFuelTariffUsdPerMmbtu * heatRate.heatRateHhvMmbtuPerMwh
      : null;

  const stackCostAccountingMode =
    config?.stackCostAccountingMode ?? 'ltsa-levelized-opex';
  const ltsaPerKwYearUsd = config?.ltsaPerKwYearUsd ?? pkg.benchmarks.ltsaPerKwYearUsd;
  const discreteRefurbishmentEvents = config?.discreteRefurbishmentEvents ?? [];

  // Validate mutual exclusion between LTSA and discrete stack replacement (Negative Test 9)
  validateStackCostAccounting({
    stackCostAccountingMode,
    ltsaPerKwYearUsd,
    discreteRefurbishmentEvents,
    capacityMw: 1,
    strict: true,
  });

  const allSiteVerified =
    !isMissingCriticalEconomics &&
    fuelPriceBasis === 'site-delivered-tariff' &&
    pressureVerified &&
    firmTransportVerified &&
    qualityVerified &&
    islandEval.islandReady;

  const economicQualificationPosture: EconomicQualificationPosture = isMissingCriticalEconomics
    ? 'unsupported-incomplete'
    : allSiteVerified
      ? 'site-qualified'
      : 'assumption-backed-benchmark';

  return {
    configId: `${pkg.packageId}:${primaryFuel}:${stackCostAccountingMode}`,
    chemistry: pkg.chemistry,
    primaryFuel,
    oemPackageId: pkg.packageId,
    oemPackageLabel: pkg.label,
    powerBoundary: ratingBoundary.powerBoundary,
    efficiencyAveragingBasis: ratingBoundary.efficiencyAveragingBasis,
    outputGuaranteeMode: ratingBoundary.outputGuaranteeMode,
    electricalEfficiencyFractionLhv: effLhv,
    hhvToLhvRatio: heatRate.hhvToLhvRatio,
    netAcHeatRateHhvBtuPerKwh: heatRate.heatRateHhvBtuPerKwh,
    netAcHeatRateHhvMmbtuPerMwh: heatRate.heatRateHhvMmbtuPerMwh,
    externalParasiticDerateFraction: pcc.externalParasiticDerateFraction,
    netDeliverableAcRatioAtPcc: pcc.netDeliverableAcRatioAtPcc,
    capexPerKwUsd: isMissingCriticalEconomics ? null : capexPerKwUsd,
    fixedOmPerKwYearUsd:
      isMissingCriticalEconomics
        ? null
        : pkg.benchmarks.fixedOmPerKwYearUsd +
          (stackCostAccountingMode === 'ltsa-levelized-opex' ? ltsaPerKwYearUsd : 0),
    variableOmPerMwhUsd: isMissingCriticalEconomics ? null : pkg.benchmarks.variableOmPerMwhUsd,
    deliveredFuelTariffUsdPerMmbtu,
    fuelPriceBasis,
    fuelCostPerMwhUsd,
    stackCostAccountingMode,
    ltsaPerKwYearUsd,
    discreteRefurbishmentEvents,
    capacityFactorPct: pkg.benchmarks.capacityFactorPct,
    capacityCreditPct: pkg.benchmarks.capacityCreditPct,
    tCo2ePerMwh: pkg.benchmarks.tCo2ePerMwh,
    landAcresPerNameplateMw: pkg.benchmarks.landAcresPerNameplateMw,
    economicQualificationPosture,
    relianceHolds: holds,
  };
}

/**
 * Build a finance `Pathway` preset for one of the 4 stationary fuel-cell
 * technology IDs (`fuel-cell-sofc`, `fuel-cell-pafc`, `fuel-cell-pem`, `fuel-cell-mcfc`).
 */
export function buildFuelCellPresetForTechnology(
  tech: 'fuel-cell-sofc' | 'fuel-cell-pafc' | 'fuel-cell-pem' | 'fuel-cell-mcfc',
  intent: ProjectIntent,
  _siteContext: SiteContext,
  options?: PathwayPresetOptions & {
    fuelCellConfig?: FuelCellPhaseConfig;
    fuelSupply?: FuelSupplyEvidence | null;
  },
): Pathway {
  const pkg = DEFAULT_PACKAGE_BY_TECHNOLOGY_ID[tech];
  const snapshot = buildFuelCellEconomicSnapshot({
    technologyId: tech,
    config: options?.fuelCellConfig,
    fuelSupply: options?.fuelSupply,
  });

  const facilityLoadMw = resolveFacilityLoadMw(intent);
  const reserveMultiplier = 1.15; // RESERVE_MARGIN_PCT = 15%
  const creditFraction = (snapshot.capacityCreditPct || pkg.benchmarks.capacityCreditPct) / 100;
  const pccRatio = snapshot.netDeliverableAcRatioAtPcc > 0 ? snapshot.netDeliverableAcRatioAtPcc : 1;
  const capacityMw = (facilityLoadMw * reserveMultiplier) / (creditFraction * pccRatio);
  const startDate = resolveProjectStartDate(intent);
  const b = pkg.benchmarks;

  const capexPerKw = snapshot.capexPerKwUsd ?? b.capexPerKwUsd;
  const fixedOmPerKwYear =
    snapshot.fixedOmPerKwYearUsd ?? b.fixedOmPerKwYearUsd + b.ltsaPerKwYearUsd;
  const variableOmPerMwh = snapshot.variableOmPerMwhUsd ?? b.variableOmPerMwhUsd;
  const fuelCostPerMwh =
    snapshot.fuelCostPerMwhUsd ??
    b.assumedBenchmarkDeliveredFuelUsdPerMmbtu * (snapshot.netAcHeatRateHhvMmbtuPerMwh ?? 6.7);

  const stackCheck = validateStackCostAccounting({
    stackCostAccountingMode: snapshot.stackCostAccountingMode,
    ltsaPerKwYearUsd: snapshot.ltsaPerKwYearUsd,
    discreteRefurbishmentEvents: snapshot.discreteRefurbishmentEvents,
    capacityMw,
    strict: true,
  });

  const baseCapexUsd = capexFromPerKw(capacityMw, capexPerKw);
  const decommissioningUsd = baseCapexUsd * b.decommPctOfCapex;
  const annualMwh = annualMwhFromCf(capacityMw, b.capacityFactorPct);
  const annualOpexUsd = computeAnnualOpexUsd(
    capacityMw,
    fixedOmPerKwYear,
    variableOmPerMwh,
    fuelCostPerMwh,
    annualMwh,
  );

  const capex = buildCapexBlock(startDate, b.constructionMonths, baseCapexUsd, {
    contingencyPct: b.contingencyPct,
  });
  const codYear =
    new Date(startDate).getUTCFullYear() + Math.ceil(b.constructionMonths / 12);

  const base: Pathway = {
    id: pathwayIdFromTechnology(tech),
    name: `BTM ${pkg.label}`,
    classification: 'btm',
    technology: tech,
    capacityMW: capacityMw,
    facilityLoadMW: facilityLoadMw,
    economicQualificationPosture: snapshot.economicQualificationPosture,
    fuelCellSnapshot: snapshot,
    relianceHolds: snapshot.relianceHolds,
    phases:
       snapshot.economicQualificationPosture === 'unsupported-incomplete'
        ? []
        : buildStandardBtmPhases({
            startDate,
            constructionMonths: b.constructionMonths,
            designLifeYears: b.designLifeYears,
            totalCapexUsd: baseCapexUsd,
            decommissioningUsd,
            annualMwh,
            annualOpexUsd,
          }),
    capex,
    opex: {
      fixedOmPerKwYearUsd: fixedOmPerKwYear,
      variableOmPerMwhUsd: variableOmPerMwh,
      fuelCostPerMwhUsd: fuelCostPerMwh,
      fuelPriceCagrPct: DEFAULT_GAS_FUEL_PRICE_CAGR_PCT,
      fuelPriceStdev: 4,
      ...(stackCheck.effectiveRefurbishmentEvents.length > 0
        ? { refurbishmentEvents: stackCheck.effectiveRefurbishmentEvents }
        : {}),
    },
    revenue: valueOfServedLoadRevenue({
      termYears: b.designLifeYears,
      availabilityPct: b.availabilityPct,
    }),
    risk: {
      technologyMaturity: 'commercial-but-evolving',
      regulatoryRisk: 'medium',
      fuelPriceRisk: pkg.primaryFuel === 'hydrogen' ? 'high' : 'medium',
      baseWaccPct: DEFAULT_BASE_WACC_PCT,
      riskPremiumBps: b.riskPremiumBps,
    },
    carbon: {
      tCo2ePerMwh: b.tCo2ePerMwh,
    },
    lifetime: {
      designLifeYears: b.designLifeYears,
      decommissioningUsd,
      decommissioningYear: codYear + b.designLifeYears,
    },
  };

  return mergePathway(base, options?.overrides);
}

export type FuelCellPresetOptions = PathwayPresetOptions & {
  fuelCellConfig?: FuelCellPhaseConfig;
  fuelSupply?: FuelSupplyEvidence | null;
};

export const fuelCellSofcPresetFor = (
  intent: ProjectIntent,
  siteContext: SiteContext,
  options?: FuelCellPresetOptions,
): Pathway => buildFuelCellPresetForTechnology('fuel-cell-sofc', intent, siteContext, options);

export const fuelCellPafcPresetFor = (
  intent: ProjectIntent,
  siteContext: SiteContext,
  options?: FuelCellPresetOptions,
): Pathway => buildFuelCellPresetForTechnology('fuel-cell-pafc', intent, siteContext, options);

export const fuelCellPemPresetFor = (
  intent: ProjectIntent,
  siteContext: SiteContext,
  options?: FuelCellPresetOptions,
): Pathway => buildFuelCellPresetForTechnology('fuel-cell-pem', intent, siteContext, options);

export const fuelCellMcfcPresetFor = (
  intent: ProjectIntent,
  siteContext: SiteContext,
  options?: FuelCellPresetOptions,
): Pathway => buildFuelCellPresetForTechnology('fuel-cell-mcfc', intent, siteContext, options);
